/**
 * The Protect board.
 *
 * Half of a negotiation is not going backwards, and that half has no natural
 * home in a tool otherwise organised around what to ask for. Under a
 * single-enterprise agreement the floor is the Award, not the agreement the
 * group works under now, so every condition above that floor is something
 * somebody has to actively decide to keep.
 *
 * Two things shape this screen.
 *
 * A card the Award is silent on sorts above one where the Award is merely
 * worse, because it is more exposed and not less: there is no floor beneath it
 * at all, so it can go to zero rather than falling to a lesser figure. That
 * reads as counter-intuitive on a board about protection, so the card says it
 * in words rather than relying on the ordering to carry the point.
 *
 * And every card states that nobody has checked it. This screen prints, and it
 * prints for mass meetings — a handout is the exact place where an unverified
 * model reading becomes something eighty people believe the union always knew.
 */

import { useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExportSelection } from '../claims/ExportSelection.js';
import { api, type ProtectCard } from '../lib/api.js';
import { FilterBox, useFilter } from '../shell/Filter.js';

function Exposure({ card }: { card: ProtectCard }) {
  if (card.exposure === 'full') {
    /*
      The two exposures are the whole board, and they were two coloured
      sentences differing only in hue and wording. Given as a band with a
      glyph, "there is nothing underneath this" is visible from across the
      room — which is where it gets read, because this board is printed and
      taken into meetings.
    */
    return (
      <div
        className="callout callout-strong"
        style={{ '--callout-hue': 'var(--color-defect)' } as CSSProperties}
      >
        <span className="callout-glyph" aria-hidden>
          ▼
        </span>
        <p className="text-[12px] leading-snug">
          <span className="font-medium">Nothing is underneath this — not the Award, not the law.</span>{' '}
          If it is not carried into the new agreement it goes entirely.
        </p>
      </div>
    );
  }

  /*
    The band that was missing, and the reason the strong one above can be
    trusted now.

    `full` used to mean "the Award is silent", and the board rendered that as
    "there is no legal minimum underneath it". Twelve of the eighteen cards
    saying so were wrong: severance pay falls to NES ss.119-123, notice to
    s.117, personal leave to s.96, long service leave to the Long Service Leave
    Act 1955 (NSW). Printed and carried into a meeting, that is the
    overstatement an employer's lawyer corrects in front of the room, and it
    spends the credibility the five genuine cards need.

    Named provisions rather than a hedge, because "there may be a statutory
    minimum" is worse than either claim — it tells a representative to go and
    look something up in the middle of an argument.
  */
  if (card.exposure === 'statute') {
    return (
      <div className="callout" style={{ '--callout-hue': 'var(--color-behind)' } as CSSProperties}>
        <span className="callout-glyph" aria-hidden>
          ◈
        </span>
        <p className="text-[12px] leading-snug">
          <span className="font-medium">The Award is silent, but the law is not.</span>{' '}
          {card.statutoryFloors.map((f) => `${f.provision} gives ${f.gives}`).join('; ')}. What is
          at risk is what we have above that.
        </p>
        {/*
          The caveat that used to sit here is gone, and its removal is the point
          of loading the Act.

          It read "cited from the legislation, not from a document in this
          workbench — check before relying on it", because these citations were
          section numbers written from recollection with nothing to check them
          against. The Fair Work Act and the Long Service Leave Act 1955 (NSW)
          are now in the corpus and an integration test resolves every citation
          against it, so the sentence is no longer true and the caveat would
          now be the misleading part.
        */}
      </div>
    );
  }

  if (card.exposure === 'unchecked') {
    return (
      <div className="callout" style={{ '--callout-hue': 'var(--color-muted)' } as CSSProperties}>
        <span className="callout-glyph" aria-hidden>
          ◔
        </span>
        <p className="text-[12px] leading-snug">
          <span className="font-medium">Nobody has worked out what sits underneath this.</span> One
          of its entitlements is not in the statutory list, so the board will not guess either way.
        </p>
      </div>
    );
  }
  return (
    <div className="callout" style={{ '--callout-hue': 'var(--color-behind)' } as CSSProperties}>
      <span className="callout-glyph" aria-hidden>
        ◣
      </span>
      <p className="text-[12px] leading-snug">
        <span className="font-medium">
          The Award floor is lower{card.awardFloor ? ` — clause ${card.awardFloor.number}` : ''}.
        </span>{' '}
        If it is not carried across, this falls back to the Award's version rather than
        disappearing.
      </p>
    </div>
  );
}

function Card({ card, onOpenClause }: { card: ProtectCard; onOpenClause: (n: string) => void }) {
  return (
    /*
     * A card, not a row.
     *
     * These were hairline-separated rows in one running column, which on a
     * board of thirty-five conditions gives no boundary stronger than the rule
     * between the exposure callout and the plain-English note *inside* one
     * card — so a condition and its neighbour's caveat read as one item. The
     * shared `.card` is the 8px hairline the rest of the app uses; the padding
     * comes with it rather than being re-decided here.
     */
    <article className="protect-card card break-inside-avoid">
      <header className="card-header">
        <div className="flex min-w-0 items-baseline gap-2">
          <button
            type="button"
            onClick={() => onOpenClause(card.number)}
            className="clause-number shrink-0 text-[12px] underline decoration-[var(--color-rule-strong)] underline-offset-2"
          >
            {card.number}
          </button>
          <h3 className="min-w-0 text-[13.5px] font-semibold leading-snug tracking-tight">
            {card.heading}
          </h3>
        </div>
        {!card.fullyChecked && (
          /*
            On every card, and on the printout. See the note at the top.

            The shared `.stamp-ai` rather than a hand-written glyph: this is the
            single most important qualifier in the product — no person has stood
            behind the comparison the card rests on — and it is the same claim
            the rest of the app makes with the same mark. It was a 9px mono
            label distinguished from its neighbours by colour alone.
          */
          <span
            className="stamp stamp-ai shrink-0"
            style={{ color: 'var(--color-defect)' }}
            title="No representative has checked the comparison this card rests on"
          >
            unchecked
          </span>
        )}
      </header>

      {card.entitlements.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-x-4 gap-y-0.5">
          {card.entitlements.map((entitlement) => (
            <li key={entitlement.type} className="text-[12.5px]">
              {/* A figure is data. §7 sets every figure in tabular mono, and
                  these were sans bold — so the quantum read as emphasis rather
                  than as a quantity, and a column of them did not align. */}
              <span className="font-mono text-[12px] font-medium tabular-nums">
                {entitlement.quantum ? `${entitlement.quantum} ` : ''}
                {entitlement.unit ?? ''}
              </span>{' '}
              {entitlement.type}
              {entitlement.cumulative === true && (
                <span className="text-[var(--color-muted)]"> · accumulates</span>
              )}
            </li>
          ))}
        </ul>
      )}

      <Exposure card={card} />

      {card.plain && (
        <p className="measure mt-2 text-[12.5px] leading-relaxed text-[var(--color-muted)]">
          {card.plain}
        </p>
      )}

      {card.watch && (
        // What the employer will say. A rep who has heard the counter-argument
        // before the meeting answers it; one who has not concedes the point.
        <p className="measure mt-2 text-[12.5px] leading-snug">
          <span className="label">They will argue · </span>
          {card.watch}
        </p>
      )}

      <Actions card={card} />
    </article>
  );
}

/**
 * The board is where the checking happens.
 *
 * Protect was the only surface that showed something urgent and offered no way
 * to act on it. Every card said "the Award floor has nothing under this" and
 * "◔ unchecked", and there was nothing to press — the sole route to
 * comparison_verified was a separate queue, which is why none of the
 * thirty-five had been done and why the Plain surface, which gates on exactly
 * this flag, rendered empty for a hundred and twenty people.
 *
 * Confirming is one press because the card already shows what is being
 * confirmed: the exposure, the Award floor it rests on, and the delta note. A
 * dialog asking "are you sure" would add a step and no information.
 *
 * Disagreeing is not the same shape and does not pretend to be. It opens the
 * clause, because saying the comparison is wrong means saying what is right,
 * and that needs both texts in front of you.
 */
function Actions({ card }: { card: ProtectCard }) {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const confirm = useMutation({
    mutationFn: () =>
      api.review({
        entityTable: 'alignment',
        entityId: card.alignmentId,
        verdict: 'accepted',
        note: `Confirmed from the Protect board: ${card.number}`,
      }),
    // Both, because Plain reads the same flag and a rep who checks a card
    // should see the count of what is still withheld drop.
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['protect'] });
      void client.invalidateQueries({ queryKey: ['plain'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (card.comparisonVerified) {
    return (
      <p className="stamp stamp-verified mt-3" style={{ color: 'var(--color-win)' }}>
        checked by a representative
      </p>
    );
  }

  return (
    /*
     * Below the card's own rule, and off the printout.
     *
     * The rule keeps the two presses attached to the condition they act on —
     * with cards butted against each other by a single hairline, a button row
     * sat as close to the next condition as to its own. `no-print` because a
     * handout is the document and not the tool: thirty-five pairs of dead
     * controls is the same noise the rail, the mode nav and the print button
     * itself are already dropped for.
     */
    <div className="no-print section-rule mt-3 flex flex-wrap items-center gap-3 pt-2.5">
      <button
        type="button"
        onClick={() => confirm.mutate()}
        disabled={confirm.isPending}
        className="hit rounded-[6px] border border-[var(--color-ink)] px-3 py-1.5 text-[12.5px]"
      >
        {confirm.isPending ? 'Saving…' : 'This is right'}
      </button>
      <button
        type="button"
        onClick={() => {
          window.location.hash = `#/verify/c/${encodeURIComponent(card.number)}`;
        }}
        className="hit rounded-[6px] border border-[var(--color-rule-strong)] px-3 py-1.5 text-[12.5px]"
      >
        Not right — open both texts
      </button>
      {error ? (
        <span className="text-[11.5px]" style={{ color: 'var(--color-defect)' }}>
          {error}
        </span>
      ) : null}
    </div>
  );
}

export function Protect({ onOpenClause }: { onOpenClause: (number: string) => void }) {
  const protect = useQuery({ queryKey: ['protect'], queryFn: api.protect });

  /*
   * Above the early returns, and it has to be.
   *
   * `useFilter` holds state, so calling it after `if (isLoading) return` means
   * React sees fewer hooks on the loading render than on the one after it and
   * throws "Rendered more hooks than during the previous render" — which takes
   * the whole board down, not just the filter. It read fine, typechecked
   * clean, and only the browser found it.
   *
   * Filtered before partitioning, so the section counts describe what is on
   * screen. Partitioning first and filtering each section would leave three
   * headings whose numbers add up to more than the cards beneath them.
   */
  const allCards = protect.data?.cards ?? [];
  const { query, setQuery, filtered: cards, total } = useFilter(allCards, (card) => [
    card.number,
    card.heading,
    card.deltaNote,
    card.plain,
    ...card.entitlements.map((e) => e.type),
    ...card.statutoryFloors.map((f) => f.provision),
  ]);

  if (protect.isLoading) {
    return <p className="screen label">Loading…</p>;
  }
  const data = protect.data;
  if (!data) return null;

  const full = cards.filter((c) => c.exposure === 'full');
  const statute = cards.filter((c) => c.exposure === 'statute');
  const unchecked = cards.filter((c) => c.exposure === 'unchecked');
  const partial = cards.filter((c) => c.exposure === 'partial');

  return (
    /*
     * `.screen` and `.screen-column` rather than `px-6 py-6 max-w-[820px]`.
     *
     * Both print overrides sit on `.protect-board` and are unlayered, so they
     * still win over the component layer these primitives live in — the board
     * loses its padding and its measure on paper exactly as before.
     */
    <div className="protect-board screen screen-column">
      <header className="mb-6">
        <div className="flex items-baseline justify-between gap-4">
          {/* The mockup sets this at 25/700/-0.025em, and it was 15px medium —
              the size of a panel label. This is the one line an eighty-person
              meeting reads off a projected page. */}
          <h1 className="text-[25px] font-semibold leading-[1.15] tracking-tight">
            What we already have
          </h1>
          <button
            type="button"
            onClick={() => window.print()}
            className="hit label no-print shrink-0 underline underline-offset-2"
          >
            Print for a meeting
          </button>
        </div>
        <p className="measure mt-2 text-[13.5px] leading-relaxed text-[var(--color-muted)]">
          Bargaining a single-enterprise agreement replaces the floor. It becomes the{' '}
          {data.floor}, not the agreement we work under now — so every condition below is something
          that has to be actively kept, not something we already own.
        </p>
        <p className="measure mt-2.5 text-[12.5px] leading-snug text-[var(--color-defect)]">
          {data.counts.checked} of {data.cards.length} have been checked by a representative.
          Everything else is drafted by software and unread.
        </p>

        {/*
          Beside Print, not instead of it, and directly under the count of what
          nobody has checked — because that count is the thing that decides
          whether the export is refused, and a rep who presses the button and is
          told "none of the 35 has been checked" should have just read why.

          The two acts differ. Print is this screen on paper for the room it was
          designed for. Export is a file that gets forwarded to someone who was
          never told what this tool is, which is why only one of them carries a
          name, a timestamp, a token and a front-page notice.
        */}
        <div className="no-print mt-3">
          <ExportSelection kind="protect" label="Export this board as a document" />
        </div>

        {/*
          `no-print`, because this board is printed and carried into meetings.
          A filter box on paper is a rectangle with somebody's search term
          frozen in it, and worse, a printed board filtered to four cards looks
          like the whole board.
        */}
        <div className="no-print mt-3">
          <FilterBox
            label="Filter by clause, heading, entitlement or statute"
            value={query}
            onChange={setQuery}
            total={total}
            shown={cards.length}
            noun="cards"
          />
        </div>
      </header>

      {/* The two sections are the whole argument of the board, so each opens on
          the stronger rule §7 reserves for a section boundary. Cards carry the
          hairline; only these two carry the boundary. */}
      <section className="section-rule mb-7 pt-3">
        <h2 className="label mb-1.5 text-[var(--color-defect)]">
          No floor underneath — {full.length}
        </h2>
        <p className="measure mb-3 text-[12.5px] leading-relaxed text-[var(--color-muted)]">
          Neither the Award nor any statute covers these. They are the most exposed things we have,
          and this list is short because it is the accurate one.
        </p>
        <div className="flex flex-col gap-2.5">
          {full.map((card) => (
            <Card key={card.number} card={card} onOpenClause={onOpenClause} />
          ))}
        </div>
      </section>

      {statute.length > 0 ? (
        <section className="section-rule mb-7 pt-3">
          <h2 className="label mb-1.5 text-[var(--color-behind)]">
            Only the statutory minimum underneath — {statute.length}
          </h2>
          <p className="measure mb-3 text-[12.5px] leading-relaxed text-[var(--color-muted)]">
            The Award says nothing, so these used to sit in the section above. The NES or a NSW Act
            still applies to each of them, and what is at risk is the distance between that minimum
            and what we have now — which for long service leave and severance is most of it.
          </p>
          <div className="flex flex-col gap-2.5">
            {statute.map((card) => (
              <Card key={card.number} card={card} onOpenClause={onOpenClause} />
            ))}
          </div>
        </section>
      ) : null}

      {unchecked.length > 0 ? (
        <section className="section-rule mb-7 pt-3">
          <h2 className="label mb-1.5 text-[var(--color-muted)]">
            Floor not worked out — {unchecked.length}
          </h2>
          <p className="measure mb-3 text-[12.5px] leading-relaxed text-[var(--color-muted)]">
            One or more of the entitlements on these clauses is not in the statutory list, so
            nothing is claimed about them either way.
          </p>
          <div className="flex flex-col gap-2.5">
            {unchecked.map((card) => (
              <Card key={card.number} card={card} onOpenClause={onOpenClause} />
            ))}
          </div>
        </section>
      ) : null}

      <section className="section-rule pt-3">
        <h2 className="label mb-1.5 text-[var(--color-behind)]">
          Better than the floor — {partial.length}
        </h2>
        <p className="measure mb-3 text-[12.5px] leading-relaxed text-[var(--color-muted)]">
          The Award covers these but gives less. Losing them means falling back to the Award's
          version rather than losing them outright.
        </p>
        <div className="flex flex-col gap-2.5">
          {partial.map((card) => (
            <Card key={card.number} card={card} onOpenClause={onOpenClause} />
          ))}
        </div>
      </section>
    </div>
  );
}
